package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.domain.repository.ContentRepository
import com.iptvplayer.shared.domain.repository.WatchHistoryRepository
import com.iptvplayer.shared.presentation.state.ContentIssue
import com.iptvplayer.shared.presentation.state.HomeCategoryRow
import com.iptvplayer.shared.presentation.state.HomeUiState
import com.iptvplayer.shared.presentation.state.toContentIssue
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

/**
 * Home only ever needs the top of the catalog (a few shelves of ~20–30 items),
 * so it consumes the windowed browse* flows instead of the full catalog. On
 * DB-backed platforms these are indexed SQL windows; on web they fall back to
 * slicing the in-RAM catalog — either way Home no longer retains thousands of
 * items in its UI state.
 */
class HomeViewModel(
    private val contentRepository: ContentRepository,
    private val watchHistoryRepository: WatchHistoryRepository
) : ViewModel() {

    private val _state = MutableStateFlow(HomeUiState())
    val state: StateFlow<HomeUiState> = _state.asStateFlow()

    /** What the sync is doing right now (source / series progress), null when idle. */
    val syncStatus: StateFlow<String?> = contentRepository.syncStatus
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), null)

    private companion object {
        const val ROW_SIZE = 30L // biggest shelf shown by any client (TV/web take 30)
        const val CATEGORY_ROWS = 5
        const val CATEGORY_ROW_SIZE = 10L
    }

    init {
        observeContent()
        loadSources(force = false)
        // Pull watch history from the backend so "Continue Watching" appears
        // on a fresh app launch (the in-memory flow is otherwise empty until
        // something is played in this session).
        syncHistoryWithRetry()
    }

    /**
     * One shot used to be enough only on a good network: at cold start the
     * request often races connectivity / token restore and fails, leaving
     * Continue Watching empty for the whole session. Retry with backoff.
     */
    private fun syncHistoryWithRetry() {
        viewModelScope.launch {
            var attempt = 0
            while (watchHistoryRepository.syncHistory() is Result.Error && attempt < 5) {
                attempt++
                delay(1_500L * attempt)
            }
        }
    }

    /** Observe windowed content flows once; they update as sources sync. */
    @OptIn(ExperimentalCoroutinesApi::class)
    private fun observeContent() {
        viewModelScope.launch {
            contentRepository.browseChannels(null, null, ROW_SIZE).collect { channels ->
                _state.update { it.copy(channels = channels) }
            }
        }
        viewModelScope.launch {
            contentRepository.browseVod(null, null, ROW_SIZE).collect { vodItems ->
                _state.update { it.copy(vodItems = vodItems) }
            }
        }
        viewModelScope.launch {
            contentRepository.browseSeries(null, null, ROW_SIZE).collect { series ->
                _state.update { it.copy(series = series) }
            }
        }
        // Category shelves: first channels of the first few channel groups.
        viewModelScope.launch {
            contentRepository.channelGroups()
                .map { it.take(CATEGORY_ROWS) }
                .distinctUntilChanged()
                .flatMapLatest { groups ->
                    if (groups.isEmpty()) {
                        flowOf(emptyList())
                    } else {
                        combine(
                            groups.map { group ->
                                contentRepository.browseChannels(group, null, CATEGORY_ROW_SIZE)
                                    .map { channels -> HomeCategoryRow(group, channels) }
                            }
                        ) { rows -> rows.filter { it.channels.isNotEmpty() } }
                    }
                }
                .collect { rows -> _state.update { it.copy(categoryRows = rows) } }
        }
        viewModelScope.launch {
            watchHistoryRepository.getHistory(10).collect { history ->
                _state.update { it.copy(continueWatching = history) }
            }
        }
    }

    // isLoading tracks the actual backend sync (not the cached flow emissions),
    // so the loading indicator stays up until content is really fetched.
    private fun loadSources(force: Boolean) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, issue = null) }
            val result = contentRepository.syncSources(force = force)
            _state.update {
                it.copy(
                    isLoading = false,
                    // Surface *why* content is missing (no sources, provider
                    // down, bad credentials…) instead of a silently empty Home.
                    issue = when (result) {
                        is Result.Success -> result.data.toContentIssue()
                        is Result.Error -> ContentIssue.SyncFailed(result.message)
                        else -> null
                    }
                )
            }
            // Populate the series shelf in the background (cheap when already
            // cached; per-category fetch otherwise). Home shouldn't block on it.
            launch { contentRepository.ensureSeriesLoaded() }
        }
    }

    fun refresh() {
        loadSources(force = true)
        syncHistoryWithRetry()
    }

    fun dismissIssue() {
        _state.update { it.copy(issue = null) }
    }
}
